/**
 * One visual-viewport + keyboard source for the store builder.
 *
 * Mobile browsers keep `position: fixed` on the *layout* viewport while the
 * keyboard shrinks the *visual* viewport. Several chrome layers used to each
 * listen to `visualViewport` and recompute that gap — Navbar, this plugin,
 * keyboard compensation, sheets — so one tap produced a cascade of jumps.
 *
 * This module is the only writer of the CSS variables those layers consume.
 * Updates are batched to one animation frame so the composer can follow the
 * keyboard as the browser reports intermediate sizes.
 */
export type StoreVisibleFrame = {
  top: number
  left: number
  w: number
  h: number
}

export type StoreVisualViewportSnapshot = {
  layoutWidth: number
  layoutHeight: number
  visualWidth: number
  visualHeight: number
  offsetTop: number
  offsetLeft: number
  pageTop: number
  keyboardInset: number
  /**
   * Which surface the keyboard belongs to. Both flags are part of the snapshot
   * so moving focus between the composer and a page field still notifies
   * subscribers even when the viewport numbers do not change.
   *
   * `pageFieldFocused` is the positive signal used to stand the composer down.
   * The inverse (`!composerFocused`) is not safe: on a second tap iOS often
   * reports the keyboard before `activeElement` is the textarea, and hiding
   * the composer in that gap unmounts the field (`display: none`) so it never
   * receives focus and stays gone.
   */
  composerFocused: boolean
  pageFieldFocused: boolean
  scrollX: number
  scrollY: number
}

/** The bottom AI composer. Focus anywhere inside it is the composer's keyboard. */
const COMPOSER_HOST = '.store-owner-ai-prompt'

const SKIP_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
])

function isTextField(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  // Opt-out: in-place editors that must not raise the soft keyboard (footer copy).
  if (el.closest('[data-soft-keyboard="0"], [inputmode="none"]')) return false
  if (el.getAttribute('inputmode') === 'none') return false
  if (el.dataset.softKeyboard === '0') return false
  if (el instanceof HTMLTextAreaElement) return !el.disabled && !el.readOnly
  if (el instanceof HTMLSelectElement) return !el.disabled
  if (el instanceof HTMLInputElement) {
    if (el.disabled || el.readOnly) return false
    if ((el.getAttribute('inputmode') || '').toLowerCase() === 'none') return false
    return !SKIP_INPUT_TYPES.has((el.type || 'text').toLowerCase())
  }
  if (el.isContentEditable) return true
  const host = el.closest('[contenteditable]:not([contenteditable="false"])')
  return host instanceof HTMLElement && host.isContentEditable
}

function isComposerEl(el: EventTarget | null): boolean {
  return el instanceof HTMLElement && Boolean(el.closest(COMPOSER_HOST))
}

/** Ignore Flutter / first-paint visualViewport boxes. */
const MIN_RELIABLE_VV = 80
/**
 * Sub-pixel rounding and iOS overscroll wobble. Not a guess at keyboard
 * height — anything larger than this is published continuously.
 */
const KB_INSET_NOISE = 8
const PINCH_SCALE_SLACK = 0.04

const emptySnapshot = (): StoreVisualViewportSnapshot => ({
  layoutWidth: 0,
  layoutHeight: 0,
  visualWidth: 0,
  visualHeight: 0,
  offsetTop: 0,
  offsetLeft: 0,
  pageTop: 0,
  keyboardInset: 0,
  composerFocused: false,
  pageFieldFocused: false,
  scrollX: 0,
  scrollY: 0,
})

/**
 * Sticky composer session. A brief `focusout` (iOS retap / keyboard reuse)
 * must not look like "the composer is gone" or the dock hides itself.
 */
let composerSession = false
let focusPump = 0

let started = false
let raf = 0
let snapshot = emptySnapshot()
const listeners = new Set<(next: StoreVisualViewportSnapshot) => void>()
/** Dev-only fake keyboard height. Production builds keep this at 0. */
let devKeyboardInset = 0
/**
 * Last full-screen layout height while no field is focused. iOS Safari often
 * shrinks `window.innerHeight` with the keyboard, which made
 * `innerHeight - visualHeight` collapse to 0 and left the composer under the
 * keys. Remember the resting height and measure the inset against that.
 */
let restLayoutHeight = 0

/**
 * Visible frame for store overlays (sheets, coach) inside Safari / Flutter WKWebView.
 *
 * Always pin to the layout origin (0,0). Only shrink width/height to the glass.
 *
 * Applying `visualViewport.offsetLeft` / `offsetTop` after focus/keyboard is what
 * shifts the Add-to-menu sheet and leaves a white strip / clipped footer — the
 * store shell already locks document scroll, so those offsets are not needed and
 * double-count the keyboard inset inside Flutter's WebView.
 *
 * Flutter's Android/iOS WebView sometimes reports a tiny or 0×scale visualViewport
 * on the first paint (or while the embed is settling). Treat those as invalid and
 * fall back to the layout viewport — otherwise fixed overlays get a few-px box and
 * absolute children (coach avatar) land at negative Y and disappear.
 */
export function resolveStoreVisibleFrame(): StoreVisibleFrame {
  const layoutW = Math.max(1, window.innerWidth || document.documentElement.clientWidth || 1)
  const layoutH = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 1)
  const vv = window.visualViewport
  if (!vv) {
    return { top: 0, left: 0, w: layoutW, h: layoutH }
  }

  const scale = Number.isFinite(vv.scale) && vv.scale > 0 ? vv.scale : 1
  const vvW = Math.round(vv.width * scale)
  const vvH = Math.round(vv.height * scale)

  // Flutter WKWebView often reports a short-but-not-tiny visualViewport on the
  // first paint (100–200px). Treating that as real collapses owner sheets.
  // Only trust a short height while a field is focused (keyboard).
  const typing = isTextField(document.activeElement) || isComposerEl(document.activeElement)
  const minH = typing
    ? MIN_RELIABLE_VV
    : Math.max(MIN_RELIABLE_VV, Math.round(layoutH * 0.55))

  const w = vvW >= MIN_RELIABLE_VV ? Math.min(vvW, layoutW) : layoutW
  const h = vvH >= minH ? Math.min(vvH, layoutH) : layoutH

  return {
    top: 0,
    left: 0,
    w: Math.max(1, w),
    h: Math.max(1, h),
  }
}

/** Undo iOS document-scroll on focus without touching `.store-site` scroll. */
export function stabilizeStoreLayoutViewport() {
  if (!document.body.classList.contains('store-shell')) return
  if (window.scrollX || window.scrollY) window.scrollTo(0, 0)
  if (document.documentElement.scrollTop) document.documentElement.scrollTop = 0
  if (document.body.scrollTop) document.body.scrollTop = 0
  const site = document.querySelector('.store-site') as HTMLElement | null
  if (site && (site.scrollLeft || site.scrollTop < 0)) {
    site.scrollLeft = 0
  }
}

function liveLayoutHeight(): number {
  return Math.max(
    window.innerHeight || 0,
    document.documentElement.clientHeight || 0,
  )
}

function isCompactViewport(): boolean {
  return window.matchMedia('(max-width: 767px)').matches
}

function readSnapshot(): StoreVisualViewportSnapshot {
  const layoutWidth = Math.max(0, window.innerWidth || document.documentElement.clientWidth || 0)
  const layoutHeight = liveLayoutHeight()
  const vv = window.visualViewport
  const visualWidth = vv && vv.width >= MIN_RELIABLE_VV ? vv.width : layoutWidth
  const visualHeight = vv && vv.height >= MIN_RELIABLE_VV ? vv.height : layoutHeight
  const offsetTop = vv && vv.height >= MIN_RELIABLE_VV ? vv.offsetTop : 0
  const offsetLeft = vv && vv.width >= MIN_RELIABLE_VV ? vv.offsetLeft : 0
  const pageTop = vv && typeof vv.pageTop === 'number' ? vv.pageTop : offsetTop
  const pinched = Boolean(vv && Math.abs((vv.scale || 1) - 1) > PINCH_SCALE_SLACK)
  const active = document.activeElement
  const composerNow = isComposerEl(active)
  const pageNow = isTextField(active) && !composerNow
  const editing = composerNow || pageNow || composerSession
  if (!editing) restLayoutHeight = layoutHeight
  else if (layoutHeight > restLayoutHeight) restLayoutHeight = layoutHeight
  const fullLayout = Math.max(restLayoutHeight, layoutHeight)
  const raw = pinched || !vv || vv.height < MIN_RELIABLE_VV
    ? 0
    : fullLayout - visualHeight - offsetTop
  const measured = raw > KB_INSET_NOISE ? Math.round(raw) : 0
  const keyboardInset = Math.max(measured, import.meta.dev ? devKeyboardInset : 0)
  if (composerNow) composerSession = true
  else if (pageNow) composerSession = false
  else if (keyboardInset <= 0) composerSession = false
  return {
    layoutWidth,
    layoutHeight: fullLayout,
    visualWidth,
    visualHeight,
    offsetTop,
    offsetLeft,
    pageTop,
    keyboardInset,
    composerFocused: composerNow || (composerSession && (keyboardInset > 0 || isCompactViewport()) && !pageNow),
    pageFieldFocused: pageNow,
    scrollX: window.scrollX || 0,
    scrollY: window.scrollY || 0,
  }
}

function sameSnapshot(a: StoreVisualViewportSnapshot, b: StoreVisualViewportSnapshot): boolean {
  return a.layoutWidth === b.layoutWidth
    && a.layoutHeight === b.layoutHeight
    && a.visualWidth === b.visualWidth
    && a.visualHeight === b.visualHeight
    && a.offsetTop === b.offsetTop
    && a.offsetLeft === b.offsetLeft
    && a.pageTop === b.pageTop
    && a.keyboardInset === b.keyboardInset
    && a.composerFocused === b.composerFocused
    && a.pageFieldFocused === b.pageFieldFocused
    && a.scrollX === b.scrollX
    && a.scrollY === b.scrollY
}

function writeCss(next: StoreVisualViewportSnapshot) {
  const root = document.documentElement
  const body = document.body
  const frame = resolveStoreVisibleFrame()
  root.style.setProperty('--store-vv-top', `${frame.top}px`)
  root.style.setProperty('--store-vv-left', `${frame.left}px`)
  root.style.setProperty('--store-vv-width', `${frame.w}px`)
  root.style.setProperty('--store-vv-height', `${frame.h}px`)
  root.style.setProperty('--store-vv-offset-top', `${next.offsetTop}px`)
  if (next.keyboardInset > 0) {
    body.style.setProperty('--store-kb-inset', `${next.keyboardInset}px`)
    body.classList.add('store-kb-open')
  }
  else {
    body.style.removeProperty('--store-kb-inset')
    body.classList.remove('store-kb-open')
  }
  /*
   * AI focus mode: the composer is the field in use AND the keyboard is up, so
   * the composer owns the bottom of the visible viewport and everything else
   * fixed down there has to yield. Derived here rather than in each consumer so
   * there is still exactly one authority on keyboard state.
   */
  body.classList.toggle(
    'store-ai-focus',
    next.composerFocused && (next.keyboardInset > 0 || isCompactViewport()),
  )
}

function flush() {
  raf = 0
  stabilizeStoreLayoutViewport()
  const next = readSnapshot()
  if (sameSnapshot(snapshot, next)) return
  writeCss(next)
  snapshot = next
  for (const listener of listeners) listener(next)
}

function schedule() {
  if (!raf) raf = window.requestAnimationFrame(flush)
}

/**
 * iOS often skips visualViewport events on the second keyboard open. Sample
 * every frame while a field is focused (or the keyboard is still settling)
 * so the composer keeps tracking instead of vanishing behind the keys.
 */
function pumpWhileEditing() {
  focusPump = 0
  flush()
  const active = document.activeElement
  const keep = snapshot.keyboardInset > 0
    || isComposerEl(active)
    || isTextField(active)
  if (keep && started) {
    focusPump = window.requestAnimationFrame(pumpWhileEditing)
  }
}

function onFocusChange() {
  schedule()
  if (!focusPump) focusPump = window.requestAnimationFrame(pumpWhileEditing)
}

export function getStoreVisualViewport(): StoreVisualViewportSnapshot {
  return snapshot
}

export function getStoreKeyboardInset(): number {
  return snapshot.keyboardInset
}

/**
 * Development fake keyboard only. No-ops in production so `--store-kb-inset`
 * still comes from the real visual viewport.
 */
export function setDevKeyboardInset(px: number) {
  if (!import.meta.dev) return
  const next = Math.max(0, Math.round(px))
  if (next === devKeyboardInset) return
  devKeyboardInset = next
  schedule()
}

export function onStoreVisualViewport(
  listener: (next: StoreVisualViewportSnapshot) => void,
): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function startStoreVisualViewport() {
  if (started || typeof window === 'undefined') return
  started = true
  // First sample on the next frame: a sync read here forced a full layout of
  // the SSR document before hydration even started.
  schedule()
  window.addEventListener('resize', schedule)
  window.visualViewport?.addEventListener('resize', schedule)
  window.visualViewport?.addEventListener('scroll', schedule)
  // Both halves of the focus move: `focusout` also covers focus being dropped
  // without anything else taking it, where no `focusin` follows.
  document.addEventListener('focusin', onFocusChange, true)
  document.addEventListener('focusout', onFocusChange, true)
}

export function stopStoreVisualViewport() {
  if (!started) return
  started = false
  if (raf) {
    window.cancelAnimationFrame(raf)
    raf = 0
  }
  window.removeEventListener('resize', schedule)
  window.visualViewport?.removeEventListener('resize', schedule)
  window.visualViewport?.removeEventListener('scroll', schedule)
  document.removeEventListener('focusin', onFocusChange, true)
  document.removeEventListener('focusout', onFocusChange, true)
  if (focusPump) {
    window.cancelAnimationFrame(focusPump)
    focusPump = 0
  }
  composerSession = false
  restLayoutHeight = 0
  devKeyboardInset = 0
  document.body.style.removeProperty('--store-kb-inset')
  document.body.classList.remove('store-kb-open')
  document.body.classList.remove('store-ai-focus')
  snapshot = emptySnapshot()
}
